import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as user from '@/lib-backend/methods/user';
import { AdminUser, AdminWallet, KycStatus } from '@/lib-backend';

export type GetUserOutcome =
    | { kind: 'ok'; user: AdminUser }
    | { kind: 'notFound' }
    | { kind: 'error' };

export type GetKycStatusOutcome =
    | { kind: 'ok'; status: KycStatus }
    | { kind: 'noStatusYet' }
    | { kind: 'unknownUser' }
    | { kind: 'error' };

export type UpdateKycStatusOutcome =
    | { kind: 'ok'; status: KycStatus }
    | { kind: 'notFound' }
    | { kind: 'error' };

export type UpdateWalletOutcome =
    | { kind: 'ok'; wallet: AdminWallet; wasAlreadyCompleted: boolean }
    | { kind: 'insufficientFunds' }
    | { kind: 'error' };

@Injectable({ providedIn: 'root' })
export class UserService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    async getUser(userId: string, realmId: string | null): Promise<GetUserOutcome> {
        try {
            const resp = await this.api.client.call(
                user.getUser({ UserId: userId, RealmId: realmId })
            );
            if (resp.Result === 'Ok') return { kind: 'ok', user: resp.User };
            if (resp.Result === 'NotFound') return { kind: 'notFound' };
            return { kind: 'error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error' };
        }
    }

    async getKycStatus(userId: string, realmId: string | null): Promise<GetKycStatusOutcome> {
        try {
            const resp = await this.api.client.call(
                user.getKycStatus({ UserId: userId, RealmId: realmId })
            );
            switch (resp.Result) {
                case 'Ok':
                    return { kind: 'ok', status: resp.KycStatus };
                case 'NoStatusYet':
                    return { kind: 'noStatusYet' };
                case 'UnknownUser':
                    return { kind: 'unknownUser' };
                default:
                    return { kind: 'error' };
            }
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error' };
        }
    }

    async updateKycStatus(
        userId: string,
        realmId: string,
        newStatus: KycStatus,
        reason: string
    ): Promise<UpdateKycStatusOutcome> {
        try {
            const resp = await this.api.client.call(
                user.updateKycStatus({
                    UserId: userId,
                    RealmId: realmId,
                    NewStatus: newStatus,
                    Reason: reason,
                })
            );
            if (resp.Result === 'Ok') return { kind: 'ok', status: resp.KycStatus };
            if (resp.Result === 'NotFound') return { kind: 'notFound' };
            return { kind: 'error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error' };
        }
    }

    async updateWallet(params: {
        userId: string;
        realmId: string;
        freeCredits: number;
        paidCredits: number;
        reason: string;
        idempotentId: string;
    }): Promise<UpdateWalletOutcome> {
        try {
            const resp = await this.api.client.call(
                user.updateWallet({
                    UserId: params.userId,
                    RealmId: params.realmId,
                    FreeCredits: params.freeCredits,
                    PaidCredits: params.paidCredits,
                    Reason: params.reason,
                    IdempotentId: params.idempotentId,
                })
            );
            if (resp.Result === 'Ok') {
                return { kind: 'ok', wallet: resp.Wallet, wasAlreadyCompleted: resp.WasAlreadyCompleted };
            }
            if (resp.Result === 'InsufficientFunds') return { kind: 'insufficientFunds' };
            return { kind: 'error' };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error' };
        }
    }
}
